import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { GoogleMap, useJsApiLoader, InfoBox } from '@react-google-maps/api';
import { VamosMapProvider } from '../../providers';
import Button from '../Button/Button';
import CustomButton from './CustomButton';

// Module-level so the identity is stable across renders. An inline object literal default would
// hand GoogleMap a new `center` every render and make it re-apply setCenter(), fighting user panning.
const DEFAULT_CENTER = {
  lat: 36.95,
  lng: -100.33,
};

const DEFAULT_CHILDREN = <></>;

const VamosMap = ({
  isEnabled,
  setIsEnabled,
  isBoundsChanged,
  isButtonClicked,
  setButtonClicked,
  height = '500px',
  width = '100%',
  center = DEFAULT_CENTER,
  zoom = 4,
  className = '',
  children = DEFAULT_CHILDREN,
  mapStyle,
  onTilesLoaded = () => {},
  onLoad = () => {},
}) => {
  const [infoBoxPosition, setInfoBoxPosition] = useState(center);
  const [showInfoBox, setShowInfoBox] = useState(false);
  const infoBoxOption = {
    closeBoxURL: '',
    enableEventPropagation: true,
    boxStyle: { width: 'auto' },
  };

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.GOOGLE_MAP_JS_API_KEY,
  });
  const options = {
    minZoom: 3,
  };

  const closeInfoBox = () => {
    setShowInfoBox(false);
  };

  const handleButtonClick = () => {
    setIsEnabled(false);
    setButtonClicked(true);
  };

  return isLoaded ? (
    <VamosMapProvider>
      <div className={className}>
        <GoogleMap
          mapContainerStyle={mapStyle || { height, width }}
          zoom={zoom}
          options={options}
          center={center}
          onRightClick={(data) => {
            setInfoBoxPosition({
              lat: data.latLng.lat(),
              lng: data.latLng.lng(),
            });
            setShowInfoBox(true);
          }}
          onTilesLoaded={onTilesLoaded}
          onLoad={onLoad}
        >
          {' '}
          {showInfoBox && (
            <InfoBox options={infoBoxOption} position={infoBoxPosition}>
              <div className="bg-vamos-white p-3 rounded-md">
                <button
                  type="button"
                  aria-label="Close"
                  onClick={closeInfoBox}
                  className="absolute top-2 right-2 text-vamos-gray hover:text-vamos-black p-2"
                >
                  ✖
                </button>
                <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 pt-4 font-sans text-sm font-normal">
                  <>
                    <div className=" text-vamos-gray">LATITUDE</div>
                    <div>{infoBoxPosition.lat}</div>
                  </>
                  <>
                    <div className="text-vamos-gray">LONGITUDE</div>
                    <div>{infoBoxPosition.lng}</div>
                  </>
                </div>
                <div className="flex justify-center gap-3 mt-4">
                  <Button
                    label="Create site"
                    size="md"
                    className="w-fit-content"
                    color="secondary"
                    onClick={() => {
                      window.open(
                        `/sites/new?lat=${infoBoxPosition.lat}&lng=${infoBoxPosition.lng}`,
                        '_blank',
                        'noreferrer',
                      );
                      setShowInfoBox(false);
                    }}
                  />
                </div>
              </div>
            </InfoBox>
          )}
          {!isEnabled && isBoundsChanged && (
            <CustomButton
              onClick={handleButtonClick}
              isButtonClicked={isButtonClicked}
            />
          )}
          {children}
        </GoogleMap>
      </div>
    </VamosMapProvider>
  ) : (
    <></>
  );
};

VamosMap.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  height: PropTypes.string,
  width: PropTypes.string,
  center: PropTypes.object,
  zoom: PropTypes.number,
  mapStyle: PropTypes.object,
  isEnabled: PropTypes.bool.isRequired,
  setIsEnabled: PropTypes.func.isRequired,
  isButtonClicked: PropTypes.bool.isRequired,
  setButtonClicked: PropTypes.func.isRequired,
  isBoundsChanged: PropTypes.bool.isRequired,
  onTilesLoaded: PropTypes.func,
  onLoad: PropTypes.func,
};

export default React.memo(VamosMap);
